import { z } from 'zod';
import { inferApiResultSchema } from './infer-api';

export const DemandItemSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  demandId: z.number(),
  parentId: z.number().int().positive().nullable(),
  title: z.string().trim().min(1).max(300),
  itemTypeId: z.number().int().positive(),
  itemStoreItemId: z.number().int().positive().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type DemandItemDto = z.infer<typeof DemandItemSchema>;

export const GetDemandItemsResponseSchema = inferApiResultSchema(z.array(DemandItemSchema));
export type GetDemandItemsResponseDto = z.infer<typeof GetDemandItemsResponseSchema>;

export const GetDemandItemResponseSchema = inferApiResultSchema(DemandItemSchema);
export type GetDemandItemResponseDto = z.infer<typeof GetDemandItemResponseSchema>;

// parentId and itemStoreItemId are nullable AND optional: omitting them leaves the value alone on
// an update, while an explicit null promotes the item to a root or clears the item-store link.
export const CreateDemandItemSchema = DemandItemSchema.pick({
  title: true,
  itemTypeId: true,
  parentId: true,
  itemStoreItemId: true,
}).partial({ parentId: true, itemStoreItemId: true });
export type CreateDemandItemDto = z.infer<typeof CreateDemandItemSchema>;

export const CreateDemandItemResponseSchema = inferApiResultSchema(DemandItemSchema);
export type CreateDemandItemResponseDto = z.infer<typeof CreateDemandItemResponseSchema>;

export const UpdateDemandItemSchema = CreateDemandItemSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  { message: 'At least one demand item field must be provided' },
);
export type UpdateDemandItemDto = z.infer<typeof UpdateDemandItemSchema>;

export const UpdateDemandItemResponseSchema = inferApiResultSchema(DemandItemSchema);
export type UpdateDemandItemResponseDto = z.infer<typeof UpdateDemandItemResponseSchema>;

// Exact wording for the 400/503 errors `apps/fim`'s DemandItemService throws for validation
// failures (see demand-item.service.ts). Shared here - rather than duplicated as inline string
// literals in both the backend's `throw` sites and the frontend's message-classification regexes
// (work-item-hierarchy/demandItemErrors.ts) - so a future wording change can't silently drift the
// two out of sync: both sides import the same source of truth.
export const DemandItemErrorMessages = {
  itemTypeValidationUnavailable: 'Item type validation is temporarily unavailable; please retry',
  unknownItemType: (itemTypeId: number) => `Unknown item type ${itemTypeId}`,
  itemTypeArchived: (itemTypeId: number) => `Item type ${itemTypeId} is archived`,
  cannotBeOwnParent: 'A demand item cannot be its own parent',
  parentNotFound: (parentId: number, demandId: number) =>
    `Parent demand item ${parentId} was not found in demand ${demandId}`,
  cannotMoveUnderDescendant: 'A demand item cannot be moved underneath one of its own descendants',
  maxNestingDepth: (parentId: number, maxDepth: number) =>
    `Parent demand item ${parentId} is already at the maximum supported nesting depth of ${maxDepth}`,
} as const;
